import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  FolderIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { apiClient } from '../../../../config/api';
import { MatterDetailed } from '../../../../types/matters';
import { useMatterBilling } from '../../hooks/useMatterBilling';

interface OverviewTabProps {
  matter: MatterDetailed;
}

interface RelatedWorkSummary {
  engagement: {
    id: string;
    engagementNumber: string;
    title: string;
    status: string;
    priority: string;
    estimatedValue?: number | null;
    createdAt: string;
    updatedAt: string;
  };
  contact: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
  } | null;
  counts: {
    tasks: number;
    openTasks: number;
    documents: number;
    notes: number;
    communications: number;
    unreadCommunications: number;
    upcomingEvents: number;
    bills: number;
  };
  financials: {
    totalBilled: number;
    totalPaid: number;
    outstanding: number;
    unbilledTime: number;
    unbilledExpenses: number;
  };
  schedule: {
    nextEvent: RelatedWorkEvent | null;
    lastEvent: RelatedWorkEvent | null;
  };
  recentActivity: RelatedWorkActivity[];
}

interface RelatedWorkEvent {
  id: string;
  title: string;
  startsAt: string;
  type: string | null;
}

interface RelatedWorkActivity {
  id: string;
  type: 'task' | 'document' | 'note' | 'communication' | 'event' | 'billing';
  title: string;
  occurredAt: string;
  description: string | null;
}

const formatCurrency = (amount?: number | null) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(amount || 0));

const formatDate = (value?: string | null) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getResponseData = <T,>(response: any): T => {
  if (response?.data) return response.data as T;
  return response as T;
};

const StatCard = ({
  label,
  value,
  icon: Icon,
  to,
  tone = 'default',
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  to?: string;
  tone?: 'default' | 'attention';
}) => {
  const content = (
    <div className={`rounded-lg border p-4 transition-colors ${
      tone === 'attention'
        ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100'
        : 'border-gray-200 bg-white text-gray-900 hover:border-lex-accent dark:border-gray-700 dark:bg-gray-800 dark:text-white'
    }`}>
      <div className="mb-3 flex items-center justify-between">
        <Icon className="h-5 w-5 text-lex-accent" />
        <span className="text-2xl font-semibold">{value}</span>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  );

  return to ? <Link to={to}>{content}</Link> : content;
};

const OverviewTab: React.FC<OverviewTabProps> = ({ matter }) => {
  const { stats: billingStats } = useMatterBilling(matter.id);
  const [relatedWork, setRelatedWork] = useState<RelatedWorkSummary | null>(null);
  const [loadingRelatedWork, setLoadingRelatedWork] = useState(true);
  const [relatedWorkError, setRelatedWorkError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoadingRelatedWork(true);
    setRelatedWorkError(null);

    apiClient.get<RelatedWorkSummary>(`/api/v1/engagements/${matter.id}/related-work`)
      .then((response) => {
        if (active) setRelatedWork(getResponseData<RelatedWorkSummary>(response));
      })
      .catch(() => {
        if (active) {
          setRelatedWork(null);
          setRelatedWorkError('Unable to load engagement related work.');
        }
      })
      .finally(() => {
        if (active) setLoadingRelatedWork(false);
      });

    return () => {
      active = false;
    };
  }, [matter.id]);

  const financials = relatedWork?.financials;
  const counts = relatedWork?.counts;
  const totalBilled = financials?.totalBilled ?? billingStats?.totalBilled ?? 0;
  const outstanding = financials?.outstanding ?? billingStats?.billsOutstanding ?? 0;
  const totalPaid = financials?.totalPaid ?? billingStats?.billsPaid ?? 0;

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.7fr)]">
        <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Engagement Details</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Subscriber-owned engagement profile and contact linkage.</p>
            </div>
            <span className="rounded-full bg-lex-accent-soft px-2.5 py-1 text-xs font-semibold text-lex-accent">
              {matter.stage || matter.status || 'active'}
            </span>
          </div>

          <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Engagement number</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white">{matter.matter_number || matter.displayNumber || 'Not set'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Priority</dt>
              <dd className="mt-1 capitalize text-sm text-gray-900 dark:text-white">{matter.priority || 'medium'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Contact</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                {relatedWork?.contact ? (
                  <Link className="text-lex-accent hover:underline" to={`/contacts/${relatedWork.contact.id}`}>
                    {relatedWork.contact.name}
                  </Link>
                ) : matter.client?.name || 'Not linked'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Estimated value</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatCurrency(matter.estimated_value)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Opened</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(matter.openDate || matter.created_at)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Updated</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(matter.updated_at)}</dd>
            </div>
          </dl>

          {(matter.matter_summary || matter.description) && (
            <div className="mt-5 border-t border-gray-200 pt-4 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Summary</h4>
              <p className="mt-2 whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-300">
                {matter.matter_summary || matter.description}
              </p>
            </div>
          )}
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
          <div className="mb-4 flex items-center gap-2">
            <CurrencyDollarIcon className="h-5 w-5 text-lex-accent" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Financial Summary</h3>
          </div>
          <div className="grid gap-3">
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Total billed</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{formatCurrency(totalBilled)}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-emerald-50 p-4 dark:bg-emerald-500/10">
                <p className="text-xs font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Collected</p>
                <p className="mt-1 text-lg font-semibold text-emerald-900 dark:text-emerald-100">{formatCurrency(totalPaid)}</p>
              </div>
              <div className="rounded-lg bg-red-50 p-4 dark:bg-red-500/10">
                <p className="text-xs font-medium uppercase tracking-wide text-red-700 dark:text-red-300">Outstanding</p>
                <p className="mt-1 text-lg font-semibold text-red-900 dark:text-red-100">{formatCurrency(outstanding)}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-500/10">
                <p className="text-xs font-medium uppercase tracking-wide text-blue-700 dark:text-blue-300">Unbilled time</p>
                <p className="mt-1 text-lg font-semibold text-blue-900 dark:text-blue-100">{formatCurrency(financials?.unbilledTime)}</p>
              </div>
              <div className="rounded-lg bg-amber-50 p-4 dark:bg-amber-500/10">
                <p className="text-xs font-medium uppercase tracking-wide text-amber-700 dark:text-amber-300">Unbilled expenses</p>
                <p className="mt-1 text-lg font-semibold text-amber-900 dark:text-amber-100">{formatCurrency(financials?.unbilledExpenses)}</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {relatedWorkError && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-700 dark:text-amber-300">
          {relatedWorkError}
        </div>
      )}

      <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Related Work</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Live work connected to this engagement.</p>
          </div>
          {loadingRelatedWork && <span className="text-sm text-gray-500 dark:text-gray-400">Loading...</span>}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Open tasks" value={counts?.openTasks ?? 0} icon={CheckCircleIcon} to={`/tasks?engagementId=${matter.id}`} />
          <StatCard label="Documents" value={counts?.documents ?? 0} icon={DocumentTextIcon} to={`/documents?engagementId=${matter.id}`} />
          <StatCard label="Communications" value={counts?.communications ?? 0} icon={ChatBubbleLeftRightIcon} />
          <StatCard
            label="Unread communications"
            value={counts?.unreadCommunications ?? 0}
            icon={FolderIcon}
            tone={(counts?.unreadCommunications || 0) > 0 ? 'attention' : 'default'}
          />
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
          <div className="mb-4 flex items-center gap-2">
            <CalendarDaysIcon className="h-5 w-5 text-lex-accent" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Schedule</h3>
          </div>
          <div className="space-y-4">
            <ScheduleRow label="Next event" event={relatedWork?.schedule.nextEvent || null} />
            <ScheduleRow label="Last event" event={relatedWork?.schedule.lastEvent || null} />
            <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              Upcoming events: <span className="font-semibold text-gray-900 dark:text-white">{counts?.upcomingEvents ?? 0}</span>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
          <div className="mb-4 flex items-center gap-2">
            <ClockIcon className="h-5 w-5 text-lex-accent" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
          </div>
          {(relatedWork?.recentActivity || []).length > 0 ? (
            <div className="space-y-3">
              {relatedWork!.recentActivity.map((activity) => (
                <div key={`${activity.type}-${activity.id}`} className="flex gap-3 rounded-lg border border-gray-100 p-3 dark:border-gray-800">
                  <ActivityIcon type={activity.type} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{activity.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {activity.type} - {formatDateTime(activity.occurredAt)}
                    </p>
                    {activity.description && (
                      <p className="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">{activity.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center dark:border-gray-700">
              <UserIcon className="mx-auto mb-2 h-8 w-8 text-gray-300 dark:text-gray-600" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No recent engagement activity yet.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const ScheduleRow = ({ label, event }: { label: string; event: RelatedWorkEvent | null }) => (
  <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
    {event ? (
      <div className="mt-2">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">{event.title}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{formatDateTime(event.startsAt)}</p>
      </div>
    ) : (
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Not scheduled</p>
    )}
  </div>
);

const ActivityIcon = ({ type }: { type: RelatedWorkActivity['type'] }) => {
  const Icon = type === 'task'
    ? CheckCircleIcon
    : type === 'document'
      ? DocumentTextIcon
      : type === 'communication'
        ? ChatBubbleLeftRightIcon
        : type === 'event'
          ? CalendarDaysIcon
          : CurrencyDollarIcon;

  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-lex-accent-soft text-lex-accent">
      <Icon className="h-5 w-5" />
    </span>
  );
};

export default OverviewTab;
